/**
 * Country and City Selection Hooks
 *
 * Provides hooks for managing country and city selection in forms,
 * with search functionality and proper dependency handling.
 */

import { useState, useMemo, useCallback } from 'react';
import {
  getCountryOptions,
  getCitiesForCountry,
  searchCountries,
  searchCities,
  getCurrencyForCountryCode,
  type CountryOption,
  type CityOption,
} from '@data/countries-cities';

// ============================================================================
// Types
// ============================================================================

export interface UseCountriesResult {
  countries: CountryOption[];
  searchCountries: (query: string) => CountryOption[];
  getCountryByCode: (code: string) => CountryOption | undefined;
  isLoading: boolean;
}

export interface UseCitiesResult {
  cities: CityOption[];
  searchCities: (query: string) => CityOption[];
  isLoading: boolean;
  isEmpty: boolean;
}

export interface UseCountryCitySelectionOptions {
  defaultCountry?: string; // Country code
  enableSearch?: boolean;
  minSearchLength?: number;
}

export interface UseCountryCitySelectionResult {
  // Country selection
  countries: CountryOption[];
  selectedCountry: string | undefined;
  setSelectedCountry: (countryCode: string | undefined) => void;
  searchCountriesQuery: string;
  setSearchCountriesQuery: (query: string) => void;
  filteredCountries: CountryOption[];

  // City selection
  cities: CityOption[];
  selectedCity: string | undefined;
  setSelectedCity: (city: string | undefined) => void;
  searchCitiesQuery: string;
  setSearchCitiesQuery: (query: string) => void;
  filteredCities: CityOption[];

  // Utility functions
  getCurrencyForSelectedCountry: () => string;
  resetCitySelection: () => void;

  // State flags
  isLoadingCountries: boolean;
  isLoadingCities: boolean;
  hasCitiesForCountry: boolean;
}

// ============================================================================
// useCountries Hook
// ============================================================================

/**
 * Hook for managing country selection with search functionality
 */
export function useCountries(): UseCountriesResult {
  const countries = useMemo(() => getCountryOptions(), []);

  const searchCountriesHandler = useCallback((query: string) => {
    return searchCountries(query);
  }, []);

  const getCountryByCodeHandler = useCallback(
    (code: string) => {
      return countries.find((country) => country.value === code);
    },
    [countries],
  );

  return {
    countries,
    searchCountries: searchCountriesHandler,
    getCountryByCode: getCountryByCodeHandler,
    isLoading: false, // Static data, no loading needed
  };
}

// ============================================================================
// useCities Hook
// ============================================================================

/**
 * Hook for managing city selection for a specific country
 */
export function useCities(countryCode: string | undefined): UseCitiesResult {
  const cities = useMemo(() => {
    if (!countryCode) return [];
    return getCitiesForCountry(countryCode);
  }, [countryCode]);

  const searchCitiesHandler = useCallback(
    (query: string) => {
      if (!countryCode) return [];
      return searchCities(countryCode, query);
    },
    [countryCode],
  );

  return {
    cities,
    searchCities: searchCitiesHandler,
    isLoading: false, // Static data, no loading needed
    isEmpty: cities.length === 0,
  };
}

// ============================================================================
// useCountryCitySelection Hook (Comprehensive)
// ============================================================================

/**
 * Comprehensive hook for managing both country and city selection
 * with integrated search and dependency handling
 */
export function useCountryCitySelection(
  options: UseCountryCitySelectionOptions = {},
): UseCountryCitySelectionResult {
  const { defaultCountry, enableSearch = true, minSearchLength = 1 } = options;

  // Country state
  const [selectedCountry, setSelectedCountry] = useState<string | undefined>(
    defaultCountry,
  );
  const [searchCountriesQuery, setSearchCountriesQuery] = useState('');

  // City state
  const [selectedCity, setSelectedCity] = useState<string | undefined>();
  const [searchCitiesQuery, setSearchCitiesQuery] = useState('');

  // Get countries data
  const { countries, searchCountries: searchCountriesHandler } = useCountries();

  // Get cities data for selected country
  const { cities, searchCities: searchCitiesHandler } =
    useCities(selectedCountry);

  // Filtered countries based on search
  const filteredCountries = useMemo(() => {
    if (
      !enableSearch ||
      !searchCountriesQuery ||
      searchCountriesQuery.length < minSearchLength
    ) {
      return countries;
    }
    return searchCountriesHandler(searchCountriesQuery);
  }, [
    countries,
    searchCountriesQuery,
    enableSearch,
    minSearchLength,
    searchCountriesHandler,
  ]);

  // Filtered cities based on search
  const filteredCities = useMemo(() => {
    if (
      !enableSearch ||
      !searchCitiesQuery ||
      searchCitiesQuery.length < minSearchLength
    ) {
      return cities;
    }
    return searchCitiesHandler(searchCitiesQuery);
  }, [
    cities,
    searchCitiesQuery,
    enableSearch,
    minSearchLength,
    searchCitiesHandler,
  ]);

  // Get currency for selected country
  const getCurrencyForSelectedCountry = useCallback(() => {
    if (!selectedCountry) return 'EUR'; // Default fallback
    return getCurrencyForCountryCode(selectedCountry);
  }, [selectedCountry]);

  // Reset city selection when country changes
  const resetCitySelection = useCallback(() => {
    setSelectedCity(undefined);
    setSearchCitiesQuery('');
  }, []);

  // Handle country selection change
  const handleSetSelectedCountry = useCallback(
    (countryCode: string | undefined) => {
      setSelectedCountry(countryCode);
      // Reset city selection when country changes
      if (selectedCity) {
        resetCitySelection();
      }
    },
    [selectedCity, resetCitySelection],
  );

  return {
    // Country selection
    countries,
    selectedCountry,
    setSelectedCountry: handleSetSelectedCountry,
    searchCountriesQuery,
    setSearchCountriesQuery,
    filteredCountries,

    // City selection
    cities,
    selectedCity,
    setSelectedCity,
    searchCitiesQuery,
    setSearchCitiesQuery,
    filteredCities,

    // Utility functions
    getCurrencyForSelectedCountry,
    resetCitySelection,

    // State flags
    isLoadingCountries: false,
    isLoadingCities: false,
    hasCitiesForCountry: cities.length > 0,
  };
}

// ============================================================================
// Country/City Validation Helpers
// ============================================================================

/**
 * Hook for validating country and city selections
 */
export function useCountryCityValidation() {
  const { countries } = useCountries();

  const validateCountry = useCallback(
    (countryCode: string): boolean => {
      return countries.some((country) => country.value === countryCode);
    },
    [countries],
  );

  const validateCity = useCallback(
    (countryCode: string, cityName: string): boolean => {
      if (!countryCode || !cityName) return false;
      const cities = getCitiesForCountry(countryCode);
      return cities.some((city) => city.value === cityName);
    },
    [],
  );

  const getValidationErrors = useCallback(
    (
      countryCode?: string,
      cityName?: string,
    ): { country?: string; city?: string } => {
      const errors: { country?: string; city?: string } = {};

      if (countryCode && !validateCountry(countryCode)) {
        errors.country = 'País seleccionado no es válido';
      }

      if (countryCode && cityName && !validateCity(countryCode, cityName)) {
        errors.city =
          'Región/provincia/estado seleccionada no es válida para este país';
      }

      return errors;
    },
    [validateCountry, validateCity],
  );

  return {
    validateCountry,
    validateCity,
    getValidationErrors,
  };
}

// ============================================================================
// Export all hooks
// ============================================================================

export {
  // Direct data access (re-export from data module)
  getCountryOptions,
  getCitiesForCountry,
  searchCountries,
  searchCities,
  getCurrencyForCountryCode,
  getCountryByCode,
} from '@data/countries-cities';
